// Ops-action approval (arc 101 slice 04, doc 04 gap 7) — the proof that a
// production `ops_action` (deploy / migration / server op) is authorized to run
// on a PRODUCTION-tagged box.
//
// The doctrine: a task's outcome_kind can be `ops_action`, and when it targets a
// production box the runner REFUSES to execute it without a fresh approval —
// "gates are machines, not humans", but a production mutation is the one place we
// keep a human (or an explicit policy pre-authorization) in the loop.
//
// Two authorization sources, both resolved MOTHERSHIP-side (where the secret and
// the policy live) and shipped as this small proof on `FleetTaskAssignment`:
//
//   - `action_link` — a maintainer clicked a signed action link. The mint/verify
//     flow behind it is deleted; the source stays a named member of the wire
//     vocabulary an assignment may still carry, and the mothership attests the
//     result the same way for both sources.
//   - `policy` — repo policy pre-authorizes production ops for this lane, so
//     routine deploys don't each need a click.
//
// The box's job is narrow and local: refuse a production ops_action that carries
// no approval or a STALE one (`exp` in the past), and AUDIT the decision either
// way. `exp` is the freshness fence — an approval minted for one dispatch can't
// be replayed for a later run. Trust anchor: the box already runs whatever the
// mothership dispatches (sealed creds, envelope); this proof rides the same
// authenticated channel, adding a legible, expiry-bounded, auditable gate.

import { z } from 'zod';

/** How a production ops_action was authorized. */
export const OpsApprovalSource = {
  /** A maintainer redeemed a signed action link (human approval). */
  ActionLink: 'action_link',
  /** Repo policy pre-authorizes production ops for this task (no click needed). */
  Policy: 'policy',
} as const;
export const OpsApprovalSourceSchema = z.enum(OpsApprovalSource);
export type OpsApprovalSource = z.infer<typeof OpsApprovalSourceSchema>;

/**
 * The approval proof the assigner ships on `FleetTaskAssignment` for an approved
 * production `ops_action`. snake_case: it crosses NATS verbatim, same convention
 * as `TaskEnvelope` / `SealedLlmCredential`. Carries no secret — it ATTESTS an
 * approval the mothership already verified; the box gates on presence + freshness.
 */
export const OpsApprovalSchema = z.object({
  source: OpsApprovalSourceSchema,
  /** The redeemed action-link id (`action_link` source) — traceability in the audit trail. */
  action_id: z.string().min(1).max(128).optional(),
  /** Single-redeem id of the redeemed link (`action_link` source) — audit. */
  jti: z.string().min(8).max(128).optional(),
  /** Unix SECONDS the approval expires — the box refuses a stale approval (`exp` in the past). */
  exp: z.number().int().positive(),
});
export type OpsApproval = z.infer<typeof OpsApprovalSchema>;

/**
 * Is this approval still fresh at `nowMs` (unix milliseconds)? The single gate
 * the runner applies — `exp` is unix SECONDS, so scale before comparing. Pure,
 * so the production-ops decision is unit-testable against a matrix with no clock.
 */
export function opsApprovalValid(approval: OpsApproval, nowMs: number): boolean {
  return approval.exp * 1000 > nowMs;
}
